import { useState } from "react";
import {
  IconButton,
  TreeNode,
  useTreeMethodsContext,
} from "@fidelix/fx-miranda";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "@lingui/core/macro";
import { useSearchParams } from "react-router-dom";
import { useRequiredParams } from "../utils/url";
import { useDeleteGraphicsMutation } from "./graphics.mutation";
import { GenericModal } from "../common/generic-modal";
import { clearTreeParams } from "../helper";

type Props = {
  node: TreeNode;
  isDownloading: boolean;
};

export function DeleteGraphics({ node, isDownloading }: Props) {
  const queryClient = useQueryClient();
  const [_searchParams, setSearchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const { companyId } = useRequiredParams({
    companyId: "number",
  });
  const { mutateAsync, isPending } = useDeleteGraphicsMutation();
  const { handleNodePress, parsedParams } = useTreeMethodsContext();
  const graphicsId = node.getId();
  const levelId = node.parent!.getId();
  const onSuccess = () => {
    // If we are deleting the graphics we currently have opened in the tree
    // we have to navigate to the parent of the graphic since obviously
    // the graphic won't exist anymore after deleting it
    if (node.treeId === parsedParams.currentNode) {
      handleNodePress(node.parent);
      return;
    }

    setSearchParams((params) => {
      clearTreeParams(params);
      return params;
    });
    queryClient.invalidateQueries({
      queryKey: ["tree"],
    });
  };

  const onDeleteGraphics = async () => {
    await mutateAsync(
      {
        graphicsId,
        companyId,
        levelId,
      },
      { onSuccess },
    );
    setShowModal(false);
  };

  const toggleModalState = (isOpen: boolean) => {
    setShowModal(isOpen);
  };

  return (
    <>
      <IconButton
        size="small"
        variant="filled"
        icon="trash"
        onPress={() => toggleModalState(true)}
        isDisabled={isDownloading}
      />
      {showModal && (
        <GenericModal
          onClose={() => toggleModalState(false)}
          onContinue={onDeleteGraphics}
          title={t`Are You Sure?`}
          text={t`The selected graphic image and all associated files will be permanently deleted.`}
          isPending={isPending}
        />
      )}
    </>
  );
}
